import type {CallIntent} from '@phonehq/rtc-core';

export const MAX_RING_LIFETIME_MS = 90_000;

export interface RtcNotificationEvent {
    readonly eventId: string;
    readonly roomId: string;
    readonly sender: string;
    readonly originServerTs: number;
    readonly content: Record<string, unknown>;
    readonly ringId?: string;
    readonly dedupeKey?: string;
    readonly requireSenderInCall?: boolean;
}

export interface ParsedRtcNotification {
    readonly type: 'ring' | 'notification';
    readonly intent: CallIntent;
    readonly startedAt: number;
    readonly expiresAt: number;
}

export interface RingContext {
    readonly now: number;
    readonly localUserId: string | null;
    readonly enabled: boolean;
    readonly busy: boolean;
    readonly sessionActive: boolean;
    readonly localUserInCall: boolean;
    readonly senderInRoom: boolean;
    readonly senderInCall: boolean;
    readonly alreadyHandled: boolean;
    readonly doNotDisturb?: boolean;
}

export type RingIgnoreReason =
    | 'disabled'
    | 'own'
    | 'invalid'
    | 'expired'
    | 'joined'
    | 'duplicate'
    | 'not_targeted'
    | 'unknown_sender';

export type RingDecision =
    | {readonly kind: 'ignore'; readonly reason: RingIgnoreReason}
    | {readonly kind: 'wait'; readonly notification: ParsedRtcNotification}
    | {readonly kind: 'ring'; readonly notification: ParsedRtcNotification}
    | {readonly kind: 'notify'; readonly notification: ParsedRtcNotification; readonly downgradedFromRing: boolean};

export const parseRtcNotification = (event: RtcNotificationEvent): ParsedRtcNotification | undefined => {
    const {content} = event;
    const type = content['notification_type'];
    const senderTs = content['sender_ts'];
    const lifetime = content['lifetime'];
    if ((type !== 'ring' && type !== 'notification') || typeof senderTs !== 'number' || typeof lifetime !== 'number') {
        return undefined;
    }
    if (!Number.isFinite(senderTs) || !Number.isFinite(lifetime) || lifetime <= 0) {
        return undefined;
    }
    const intentValue = content['m.call.intent'];
    const intent: CallIntent = intentValue === 'video' ? 'video' : 'audio';
    const startedAt = Number.isFinite(event.originServerTs) && event.originServerTs > 0
        ? Math.min(senderTs, event.originServerTs)
        : senderTs;
    return {type, intent, startedAt, expiresAt: startedAt + Math.min(lifetime, MAX_RING_LIFETIME_MS)};
};

const isTargeted = (content: Record<string, unknown>, localUserId: string): boolean => {
    const mentions = content['m.mentions'];
    if (!mentions || typeof mentions !== 'object') {
        return true;
    }
    const {room, user_ids: userIds} = mentions as {room?: unknown; user_ids?: unknown};
    if (room === true || !Array.isArray(userIds)) {
        return true;
    }
    return userIds.includes(localUserId);
};

export const decideIncomingRing = (event: RtcNotificationEvent, context: RingContext): RingDecision => {
    if (!context.enabled) {
        return {kind: 'ignore', reason: 'disabled'};
    }
    if (context.alreadyHandled) {
        return {kind: 'ignore', reason: 'duplicate'};
    }
    if (!context.localUserId || event.sender === context.localUserId) {
        return {kind: 'ignore', reason: 'own'};
    }
    const notification = parseRtcNotification(event);
    if (!notification) {
        return {kind: 'ignore', reason: 'invalid'};
    }
    if (event.requireSenderInCall && !context.senderInRoom) {
        return {kind: 'ignore', reason: 'unknown_sender'};
    }
    if (!isTargeted(event.content, context.localUserId)) {
        return {kind: 'ignore', reason: 'not_targeted'};
    }
    if (context.now >= notification.expiresAt) {
        return {kind: 'ignore', reason: 'expired'};
    }
    if (context.localUserInCall) {
        return {kind: 'ignore', reason: 'joined'};
    }
    if (!context.sessionActive || (event.requireSenderInCall && !context.senderInCall)) {
        return {kind: 'wait', notification};
    }
    if (notification.type === 'ring' && !context.busy && !context.doNotDisturb) {
        return {kind: 'ring', notification};
    }
    return {kind: 'notify', notification, downgradedFromRing: notification.type === 'ring'};
};
